import React, { useState } from 'react';
import { 
  Box, Grid, Paper, FormControl, InputLabel, 
  Select, MenuItem, Button, TextField, Typography,
  useMediaQuery, useTheme, Accordion, AccordionSummary,
  AccordionDetails, IconButton, Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ru } from 'date-fns/locale';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Компонент для фильтрации статистики
const StatisticsFilters = ({ filters, onFilterChange, onApplyFilters, onResetFilters }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);
  
  // Обработчик изменения фильтров
  const handleFilterChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };
  
  // Обработчик разворачивания/сворачивания аккордеона
  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  // Мобильная версия фильтров
  const renderMobileFilters = () => (
    <Accordion 
      expanded={expanded} 
      onChange={handleAccordionChange}
      sx={{ 
        mb: 2,
        '&.MuiPaper-root': {
          boxShadow: 1,
          borderRadius: 1
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          minHeight: 48,
          '& .MuiAccordionSummary-content': { 
            margin: '8px 0' 
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterAltIcon fontSize="small" color="primary" />
          <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>
            Фильтры
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <DatePicker
              label="Начальная дата"
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <DatePicker
              label="Конечная дата"
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="group-by-label">Группировать по</InputLabel>
              <Select
                labelId="group-by-label"
                id="group-by"
                value={filters.groupBy}
                label="Группировать по"
                onChange={(e) => handleFilterChange('groupBy', e.target.value)}
              >
                <MenuItem value="hour">Часам</MenuItem>
                <MenuItem value="day">Дням</MenuItem>
                <MenuItem value="week">Неделям</MenuItem>
                <MenuItem value="month">Месяцам</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="admin-filter-label">Тип пользователя</InputLabel>
              <Select
                labelId="admin-filter-label"
                id="admin-filter"
                value={filters.isAdmin === undefined ? '' : filters.isAdmin.toString()}
                label="Тип пользователя"
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange('isAdmin', value === '' ? undefined : value === 'true');
                }}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="true">Только админы</MenuItem>
                <MenuItem value="false">Только пользователи</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="env-filter-label">Среда</InputLabel>
              <Select
                labelId="env-filter-label"
                id="env-filter"
                value={filters.excludeLocal === undefined ? '' : filters.excludeLocal.toString()}
                label="Среда"
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange('excludeLocal', value === '' ? undefined : value === 'true');
                }}
              >
                <MenuItem value="">Все посещения</MenuItem>
                <MenuItem value="true">Только реальные</MenuItem>
                <MenuItem value="false">Только локальные</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Путь страницы"
              value={filters.path || ''}
              onChange={(e) => handleFilterChange('path', e.target.value)}
              placeholder="/about, /blog, ..."
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={onResetFilters}
                sx={{ fontSize: '0.75rem' }}
              >
                Сбросить
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<FilterAltIcon />}
                onClick={onApplyFilters}
                sx={{ fontSize: '0.75rem' }}
              >
                Применить
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  // Десктопная версия фильтров
  const renderDesktopFilters = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Начальная дата"
            value={filters.startDate}
            onChange={(date) => handleFilterChange('startDate', date)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Конечная дата"
            value={filters.endDate}
            onChange={(date) => handleFilterChange('endDate', date)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel id="group-by-label">Группировать по</InputLabel>
            <Select
              labelId="group-by-label"
              id="group-by"
              value={filters.groupBy}
              label="Группировать по"
              onChange={(e) => handleFilterChange('groupBy', e.target.value)}
            >
              <MenuItem value="hour">Часам</MenuItem>
              <MenuItem value="day">Дням</MenuItem>
              <MenuItem value="week">Неделям</MenuItem>
              <MenuItem value="month">Месяцам</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel id="admin-filter-label">Тип пользователя</InputLabel>
            <Select
              labelId="admin-filter-label"
              id="admin-filter"
              value={filters.isAdmin === undefined ? '' : filters.isAdmin.toString()}
              label="Тип пользователя"
              onChange={(e) => {
                const value = e.target.value;
                handleFilterChange('isAdmin', value === '' ? undefined : value === 'true');
              }}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="true">Только админы</MenuItem>
              <MenuItem value="false">Только пользователи</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel id="env-filter-label">Среда</InputLabel>
            <Select
              labelId="env-filter-label"
              id="env-filter"
              value={filters.excludeLocal === undefined ? '' : filters.excludeLocal.toString()}
              label="Среда"
              onChange={(e) => {
                const value = e.target.value;
                handleFilterChange('excludeLocal', value === '' ? undefined : value === 'true');
              }}
            >
              <MenuItem value="">Все посещения</MenuItem>
              <MenuItem value="true">Только реальные</MenuItem>
              <MenuItem value="false">Только локальные</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label="Путь страницы"
            value={filters.path || ''}
            onChange={(e) => handleFilterChange('path', e.target.value)}
            placeholder="/about, /blog, ..."
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={onResetFilters}
            >
              Сбросить
            </Button>
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={onApplyFilters}
            >
              Применить фильтры
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      {isMobile ? renderMobileFilters() : renderDesktopFilters()}
    </LocalizationProvider>
  );
};

export default StatisticsFilters;
